import { useState } from "react";
import SignOut from "../../Pages/SignOut/SignOut";
// taken from https://overreacted.io/making-setinterval-declarative-with-react-hooks/
import { useEffect, useRef } from "react";
import { datadogLogs } from "@datadog/browser-logs";
export class ApiError extends Error {
  constructor(message, code, body) {
    super(message);
    this.message = message;
    this.code = code;
    this.body = body;
  }
}

export const copyToClipboard = (setFunction, ref) => {
  return e => {
    var textArea = document.createElement("textarea");
    textArea.innerText = ref.current.innerText;
    document.body.appendChild(textArea);
    textArea.select();

    const isSuccessful = document.execCommand("copy");
    textArea.remove();
    setFunction(isSuccessful ? "Copied!" : "failed copy!");
    setTimeout(() => setFunction(""), 3000);
  };
};

/**
 * emailToFirstName
 * @description: format an email address to first name and last name e.g. "John.Doe"
 * @param {String} event - the DOM event
 */
export const emailToFirstName = email => {
  if (!email) return "John Doe";
  let name;
  if (email.includes("@")) {
    name = email.split("@")[0];
  } else {
    name = email;
  }
  return name.slice(0, 1).toUpperCase() + name.slice(1);
};

/**
 * getInitials
 * @description: format an email address to an initials e.g. "JD"
 * @param {String} event - the DOM event
 */
export const getInitials = email => {
  const name = email.split("@")[0];
  return name.slice(0, 1).toUpperCase();
};

/**
 * emailToFirstNameLastName
 * @description: format an email address to first name, last name e.g. "John Doe"
 * @param {String} event - the DOM event
 */
export const emailToFirstNameLastName = email => {
  let firstName;
  const name = email.split("@")[0];
  firstName = name.substr(0, name.indexOf("."));
  if (name.indexOf(".") !== -1) {
    firstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
  } else {
    firstName = name.charAt(0).toUpperCase() + name.slice(1);
  }
  return firstName;
};

/**
 * formatDate
 * @description: format Database date format to calendar format e.g. "2021/07/09 09:39:22"
 * @param {String} event - the DOM event
 */
export const formatDate = date => {
  const resultDate = date.split(".")[0].replace("T", " ");
  return resultDate.split("-").join("/");
};

/**
 * formatMomentDate
 * @description: format Moment's date format to calendar format e.g. "2021/07/09 09:39:22"
 * @param {String} event - the DOM event
 */
export const formatMomentDate = date => {
  const resultDate = date
    .split(".")[0]
    .replace("T", " ")
    .split("-")
    .join("/");
  return resultDate.substr(0, resultDate.lastIndexOf("/"));
};

/**
 * formatHours
 * @description: formats time 12 hours with AM PM e.g. "2021/07/09 09:39:22 AM"
 * @param {String} event - the DOM event
 */
export const formatHours = date => {
  var d = new Date(date);
  var hh = d.getHours();
  var m = d.getMinutes();
  var s = d.getSeconds();
  var dd = "AM";
  var h = hh;
  if (h >= 12) {
    h = hh - 12;
    dd = "PM";
  }
  if (h === 0) {
    h = 12;
  }
  m = m < 10 ? "0" + m : m;
  s = s < 10 ? "0" + s : s;
  h = h < 10 ? "0" + h : h;
  var pattern = new RegExp("0?" + hh + ":" + m + ":" + s);
  return date.replace(pattern, h + ":" + m + ":" + s + " " + dd);
};

export const getDollars = num => (num != null ? `$${(num / 100).toFixed(2)}` : "N/A");

export const startCase = text => {
  return text
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .map(word => word.slice(0, 1).toUpperCase() + word.slice(1))
    .join(" ");
};

// https://stackoverflow.com/questions/13627308/add-st-nd-rd-and-th-ordinal-suffix-to-a-number
export const getGetOrdinal = n => {
  let s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/**
 * formatInputToPhone
 * @description: format an input's value into proper phone number format
 * @param {String} event - the DOM event
 */
export const formatInputToPhone = event => {
  let { value } = event.target;
  event.target.value = formatStringToPhone(value);
};

/**
 * formatStringToPhone
 * @description: format a string value into proper phone format
 * @param {String}
 */
export const formatStringToPhone = val => {
  val = val.replace(/\D/g, "");
  if (val.length > 10) val = val.substring(0, 10);
  const x = val.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  const formatted = !x[2] ? x[1] : "(" + x[1] + ") " + x[2] + (x[3] ? "-" + x[3] : "");
  return formatted;
};

export const formatToPhone = formatStringToPhone;

/**
 * trim
 * @description: trim the target's value and apply
 * @param {Object} event
 */
export const trim = event => {
  event.target.value = event.target.value.trim();
};

const createCognitoAuthorizationHeader = (tokens, useAccessToken = false) => {
  const { accessToken, idToken } = tokens;
  const token = useAccessToken ? accessToken : idToken;
  const res = {
    authorization: token ? `Bearer ${token}` : "",
    "Content-Type": "application/json"
  };
  return res;
};

const getTokens = session => {
  const res = {
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken()
  };
  return res;
};

const useFetch = (authData, useAccessToken = false) => {
  return (url, method = "get") => {
    const initialState = { data: null, error: null, fetching: false };
    const [state, setState] = useState(initialState);

    const resetState = () => setState(initialState);

    const makeRequest = async (body, query, overrideUrl = null) => {
      setState({ fetching: true });

      try {
        const tokens = getTokens(authData.signInUserSession);
        const headers = createCognitoAuthorizationHeader(tokens, useAccessToken);
        const options = {
          method,
          headers
        };

        if (overrideUrl) {
          url = overrideUrl;
        }

        if (body) {
          options.body = JSON.stringify(body);
        }

        if (query) {
          const params = new URLSearchParams(query);
          url = `${url}?${params}`;
        }

        const response = await fetch(url, options);

        if (!response.ok) {
          let error;
          let statusCode;
          let errorMsg = response.status + " Error";
          try {
            error = await response.json();
            datadogLogs.logger.log({ error });

            if (response.status === 401) {
              SignOut();
            }

            statusCode = error.code || error.statusCode;
            if (statusCode && error.message) {
              errorMsg = error.message;
            } else {
              errorMsg = error.errorMessage;
            }
          } catch (e) {
            /* do nothing */
          }
          throw new ApiError(errorMsg, statusCode, error);
        }

        const data = await response.json();

        setState({ data, fetching: false, error: null });
        return { ok: true, data };
      } catch (error) {
        datadogLogs.logger.error("API Call failed. Details: ", { error });
        setState({ error, fetching: false, data: null });
        return { ok: false, error };
      }
    };

    return [state, setState, makeRequest, resetState];
  };
};

export const makeUseFetch = (authData, useAccessToken = false) => {
  return useFetch(authData, useAccessToken);
};

export const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    const tick = () => {
      savedCallback.current();
    };
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [callback, delay]);
};

/**
 * titleCase
 * @description: Format the string to be capitalized and remove _ e.g. "this_is_test" will be "This Is Test"
 * @param {String} event - the DOM event
 */
export const titleCase = str => {
  var splitStr = str.toLowerCase().split("_");
  for (var i = 0; i < splitStr.length; i++) {
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
  }
  return splitStr.join(" ");
};

export const regex = {
  // eslint-disable-next-line no-useless-escape
  phone: /^\((\d{3})\)\s(\d{3})\-(\d{4})$/,
  routing: /^\d{9}$/,
  numbersOnly: /^[0-9]+$/,
  zip: /^\d{5}(?:[-\s]\d{4})?$/,
  ssn: /^(\d{3}-\d{2}-\d{4})|(\d{3}\d{2}\d{4})$/,
  notEmpty: /^(?!\s*$).+/,
  //RegExp(notEmpty).test(null) will convert the .test null argument to RegExp(notEmpty).test("null") which will always be true for null values
  //Added regex to also check for "null".
  notEmptyOrNull: /^(?!\s*$)(?!null$)/
};

export const emailsUnique = (emails, emails2) => {
  if (!emails2 || !emails2) {
    return true;
  }

  emails = (emails || "").split(",").map(email => email.trim().toLowerCase());
  emails2 = (emails2 || "").split(",").map(email => email.trim().toLowerCase());

  return emails.every(email => !emails2.includes(email));
};
export const toDollarString = n =>
  (n || 0).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
